<template>
    <Accordion
        iconName="zoom"
        title="Filters"
        :open="meetingSearchFilters.hasFiltersApplied"
    >
        <template #content>
            <form
                method="GET"
                :action="RoutePathAppEnum.Meetings"
            >
                <div class="grid grid-cols-12 gap-2 lg:gap-4">
                    <div class="col-span-12 lg:col-span-3">
                        <FormControl label="Search">
                            <TextInput
                                :name="
                                    RequestQueryKeyEnum.App_IndexMeetings_Filter_Search
                                "
                                placeholder="Customer name or email"
                                :value="meetingSearchFilters.search"
                            />
                        </FormControl>
                    </div>
                    <div class="col-span-12 lg:col-span-3">
                        <FormControl label="Service">
                            <SelectInput
                                :name="
                                    RequestQueryKeyEnum.App_IndexMeetings_Filter_ServiceId
                                "
                                :value="meetingSearchFilters.serviceId"
                            >
                                <option value="">All</option>
                                <template
                                    v-for="service in services"
                                    :key="service.id.value"
                                >
                                    <option :value="service.id.value">
                                        {{ service.title }}
                                    </option>
                                </template>
                            </SelectInput>
                        </FormControl>
                    </div>
                    <div class="col-span-12 lg:col-span-3">
                        <FormControl label="Team Member">
                            <SelectInput
                                :name="
                                    RequestQueryKeyEnum.App_IndexMeetings_Filter_CompanyUserId
                                "
                                :value="meetingSearchFilters.companyUserId"
                            >
                                <option value="">All</option>
                                <template
                                    v-for="companyUserAggregate in companyUserAggregates"
                                    :key="
                                        companyUserAggregate.companyUser.id
                                            .value
                                    "
                                >
                                    <option
                                        :value="
                                            companyUserAggregate.companyUser.id
                                                .value
                                        "
                                    >
                                        {{
                                            companyUserAggregate.user.getFullName()
                                        }}
                                    </option>
                                </template>
                            </SelectInput>
                        </FormControl>
                    </div>
                    <div class="col-span-12 lg:col-span-3">
                        <FormControl label="Status">
                            <SelectInput
                                :name="
                                    RequestQueryKeyEnum.App_IndexMeetings_Filter_MeetingState
                                "
                                :value="localMeetingSearchFilters.meetingState"
                            >
                                <option value="">All</option>
                                <option
                                    v-for="meetingState in MeetingStateEnumList"
                                    :value="meetingState.value"
                                >
                                    {{ meetingState.label }}
                                </option>
                            </SelectInput>
                        </FormControl>
                    </div>
                </div>
                <div class="grid grid-cols-12 gap-2 lg:gap-4">
                    <div class="col-span-12 lg:col-span-3">
                        <FormControl label="Date">
                            <SelectInput
                                :name="
                                    RequestQueryKeyEnum.App_IndexMeetings_Filter_DateType
                                "
                                v-model="localMeetingSearchFilters.dateType"
                            >
                                <option
                                    v-for="dateType in MeetingSearchFiltersDateTypeEnumList"
                                    :value="dateType.value"
                                >
                                    {{ dateType.label }}
                                </option>
                            </SelectInput>
                        </FormControl>
                    </div>
                    <div class="col-span-12 lg:col-span-3">
                        <FormControl
                            :disabled="
                                localMeetingSearchFilters.dateType !==
                                MeetingSearchFiltersDateTypeEnum.Custom
                            "
                            label="Start Date"
                        >
                            <TextInput
                                :disabled="
                                    localMeetingSearchFilters.dateType !==
                                    MeetingSearchFiltersDateTypeEnum.Custom
                                "
                                type="date"
                                :name="
                                    RequestQueryKeyEnum.App_IndexMeetings_Filter_MeetingStartAt
                                "
                                :value="
                                    meetingSearchFilters.meetingStartAtInAvailabilityTimeZone
                                "
                            />
                        </FormControl>
                    </div>
                    <div class="col-span-12 lg:col-span-3">
                        <FormControl
                            :disabled="
                                localMeetingSearchFilters.dateType !==
                                MeetingSearchFiltersDateTypeEnum.Custom
                            "
                            label="End Date"
                        >
                            <TextInput
                                :disabled="
                                    localMeetingSearchFilters.dateType !==
                                    MeetingSearchFiltersDateTypeEnum.Custom
                                "
                                type="date"
                                :name="
                                    RequestQueryKeyEnum.App_IndexMeetings_Filter_MeetingEndAt
                                "
                                :value="
                                    meetingSearchFilters.meetingEndAtInAvailabilityTimeZone
                                "
                            />
                        </FormControl>
                    </div>
                </div>
                <div class="mt-4 flex items-center gap-1">
                    <Button
                        iconName="zoom"
                        color="light"
                        class="w-full lg:w-auto"
                    >
                        Search
                    </Button>
                    <Button
                        type="button"
                        as="a"
                        :href="RoutePathAppEnum.Meetings"
                        iconName="arrow-rotate"
                        color="light"
                        class="w-full lg:w-auto"
                    >
                        Reset
                    </Button>
                </div>
                <div class="mt-4 text-sm text-gray-500">
                    {{ paginatedMeetingsData.numberOfRecords }} meetings found.
                </div>
            </form>
        </template>
    </Accordion>
</template>

<script setup lang="ts">
    import { ref, type PropType } from 'vue'
    import Accordion from '@/Shared/Component/Accordion.vue'
    import TextInput from '@/Shared/Component/Forms/TextInput.vue'
    import FormControl from '@/Shared/Component/Forms/FormControl.vue'
    import SelectInput from '@/Shared/Component/Forms/SelectInput.vue'
    import Service from '@/App/Service/Entity/Service'
    import CompanyUserAggregate from '@/App/CompanyUser/Entity/CompanyUserAggregate'
    import Button from '@/Shared/Component/Button.vue'
    import {
        MeetingSearchFiltersDateTypeEnum,
        MeetingSearchFiltersDateTypeEnumList,
        MeetingStateEnumList,
        type MeetingSearchFiltersType,
        type PaginatedResultsType,
    } from '@/Shared/AutoGenerated/Main'
    import { RoutePathAppEnum } from '@/Shared/AutoGenerated/RoutePathEnum'
    import { RequestQueryKeyEnum } from '@/Shared/AutoGenerated/RequestFieldEnum'

    const props = defineProps({
        paginatedMeetingsData: {
            type: Object as PropType<PaginatedResultsType>,
            required: true,
        },
        meetingSearchFilters: {
            type: Object as PropType<MeetingSearchFiltersType>,
            required: true,
        },
        services: {
            type: Array as PropType<Service[]>,
            required: true,
        },
        companyUserAggregates: {
            type: Array as PropType<CompanyUserAggregate[]>,
            required: true,
        },
    })

    const localMeetingSearchFilters = ref(props.meetingSearchFilters)
</script>
